import {useEffect, useRef, useState} from 'react'
import {rightArrow} from "@img";
import {img} from "@/utils/pluginImg.js";

// The /help FAQ. Every answer renders the SAME layout as the modal that covers the
// topic elsewhere in the app — the components are imported, not re-implemented, so a
// change to a policy modal updates this page for free. Nothing here OPENS a modal:
// the modal's content component is rendered inline inside the collapsed panel.
import {FlightDelayedContent} from "@/customComponents/BookRideSteps/Components/flightDelayedContent.jsx";
import PolicyCatalog from "@/customComponents/helpComponents/PolicyCatalog.jsx";
import {PayAfterDropoffContent} from "@/customComponents/BookRideSteps/modalPages/payAfterDropoff.jsx";
import {LatenessPoliciesContent} from "@/customComponents/GlobalComponents/latenessPolicies.jsx";
import {usePolicyCatalog} from "@/hooks/usePolicyCatalog";
import {catalogPolicyValues} from "@/customComponents/GlobalComponents/policyValues.js";
import {TollPoliciesContent, PickupPoliciesContent} from "@/customComponents/GlobalComponents/policiesContent.jsx";
import {DownloadAppReminderContent} from "@/customComponents/BookRideSteps/Components/downloadAppReminderContent.jsx";
import {FindingDriver} from "@/customComponents/BookRideSteps/modalPages/findingDriver.jsx";
import {KeenSlider} from "@/customComponents/BookRideSteps/Components/keenSlider.jsx";
import {PerkRow} from "@/customComponents/sharedComponents/WhyChooseUs.jsx";
import {WHY_PERKS} from "@/constants/airportPerks.js";
import {useChildSeatTypes} from "@/hooks/useSelectables";

// Waiting figures for the lateness tabs, from the CONTEXT-FREE catalog (/config/policies)
// — the one policy source that needs no trip, which is what this page lacks.
//
// The literals below are no longer the source, only the safety net: they are the design's
// 5 min / $3 per min / 10 min, kept for the moment before the catalog lands (and for a
// catalog that answers without a waiting block), so the answer never reads "FREE 0 min".
const LATENESS_POLICY_FALLBACK = {
    waitingPickupFreeSeconds: 300,
    waitingPickupRateCentsPerSecond: 5,
    waitingPickupMaxWaitSeconds: 600,
};

function LatenessFaq() {
    const {data: catalog} = usePolicyCatalog();
    const values = catalogPolicyValues(catalog);
    return <LatenessPoliciesContent policy={values.loaded ? values : LATENESS_POLICY_FALLBACK} spacing={false}/>;
}

// Just the "Oversized" row from the Why-Choose-Us perk list, rendered on its own.
// `subOnly` drops its icon and "Oversized" title: the card header above already shows
// that icon and asks the question the title would answer.
function OversizedFaq() {
    const perk = WHY_PERKS.find((p) => p.bags);
    if (!perk) return null;
    return (
        <div className="why-choose-us hopn-faq-oversized">
            <ul className="wcu-perks"><PerkRow perk={perk} subOnly/></ul>
        </div>
    );
}

// The child-seat carousel from the childSeatsDetails step. `readOnly` drops the +/-
// counter and enlarges the seat: this is a FAQ, so the carousel is here to SHOW what
// the range looks like — there is no booking to add a seat to from /help.
function ChildSeatsFaq() {
    const {data: infantsCarSeat = []} = useChildSeatTypes();
    return (
        <>
            {/* The carousel alone showed seats with no answer to the question. "Free of
                charge" matches how they're described everywhere else (whyUs, prebook
                compare, airport perks). */}
            <p className="mb-4 text-(--text-secondary)">
                Yes — we provide the following child car seats free of charge. Just choose
                the ones you need when you book.
            </p>
            <div className="hopn-carseat-section pr-0!">
                <KeenSlider items={infantsCarSeat} mobileItem="1.5" detailsView={true} itemNumber="2.5" readOnly/>
            </div>
        </>
    );
}

// `render` = the modal content component for that topic. Kept as a function so the
// panel only mounts (and only fetches, in the child-seat case) when it is opened.
const faqs = [
    {
        question: "Does hop'n track my flight if it's delayed?",
        icon: 'flightDelay.svg',
        render: () => <FlightDelayedContent/>,
    },
    {
        question: "What is hop'n's cancellation, modification, and refund policy?",
        icon: 'cancellation-changes.svg',
        render: () => <PolicyCatalog/>,
    },
    {
        question: 'Do you support oversized luggage like golf bags, bikes, or skis?',
        // Same icon the Oversized perk row inside this answer uses (WHY_PERKS), so the
        // header and the content it opens read as one thing.
        icon: 'oversize-bag.svg',
        media: ['oversize-surfboard.png', 'oversize-golf.png', 'oversize-guitar.png'],
        render: () => <OversizedFaq/>,
    },
    {
        question: 'How does "Pay After Drop-off" work?',
        // The Square mark from the answer's "Powered by Square" line — header and
        // content read as one thing, same as the Oversized row above.
        icon: 'black-square-logo.svg',
        // No title needed — the content no longer renders one (it lives in the modal
        // header now), and this card's own question header already covers it.
        render: () => <PayAfterDropoffContent/>,
    },
    {
        question: "What happens if I'm running late, or my driver is running late?",
        icon: 'stop-watch.svg',
        render: () => <LatenessFaq/>,
    },
    {
        question: 'How are toll charges handled?',
        icon: 'distance.svg',
        render: () => <TollPoliciesContent spacing={false}/>,
    },
    {
        question: 'Is my pickup guaranteed, and are there any exceptions?',
        icon: 'black-destination.svg',
        render: () => <PickupPoliciesContent spacing={false}/>,
    },
    {
        question: 'How can I track my driver or modify my booking?',
        icon: 'support.svg',
        render: () => <DownloadAppReminderContent/>,
    },
    {
        question: 'How do I find my driver at the airport?',
        icon: 'magnifier.svg',
        render: () => <FindingDriver hideHeader/>,
    },
    {
        question: 'Are child car seats provided?',
        icon: 'childSeatIcon.svg',
        render: () => <ChildSeatsFaq/>,
    },
];

// Must match the animation duration in styles.scss (.hopn-faq-reveal / -collapse):
// it is how long the closing panel is kept mounted so its exit can play.
const REVEAL_MS = 420;

export default function FaqList() {
    // One card open at a time, first open on arrival. Local state rather than headless
    // Disclosure: the cards are mutually exclusive, and each card's chrome (border
    // colour, chevron) reacts to the same flag.
    const [openIdx, setOpenIdx] = useState(0);
    // The card currently playing its COLLAPSE. A closed panel is unmounted — that is
    // what keeps a closed answer from building its content or firing its requests —
    // so an exit needs the panel held on screen for the length of the animation.
    // Tracked separately from openIdx so opening card B while card A is open plays
    // both at once: B expands as A collapses.
    const [closingIdx, setClosingIdx] = useState(null);
    const closeTimer = useRef(null);

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    const toggle = (i) => {
        const next = openIdx === i ? null : i;
        if (openIdx !== null && openIdx !== next) {
            setClosingIdx(openIdx);
            clearTimeout(closeTimer.current);
            closeTimer.current = setTimeout(() => setClosingIdx(null), REVEAL_MS);
        }
        setOpenIdx(next);
    };

    return (
        <div className="white-global-section">
            <h5 className="font-bold mb-4 max-md:mb-3! max-md:text-base!">FAQS</h5>
            {/* Card list. Same anatomy as the booking flow's Popular-questions accordion
                (icon tile ▸ question ▸ chevron, answer underneath), but each row is its
                own card and the card is drawn with a BORDER — the /help page reads as a
                flat document, so the shadowed variant used inside modals would fight the
                page rather than sit on it. */}
            <div className="flex flex-col gap-4 max-md:gap-2.5">
                {faqs.map((faq, i) => {
                    const open = openIdx === i;
                    const closing = closingIdx === i;
                    return (
                        <div
                            key={i}
                            className={`rounded-2xl border transition-colors ${open ? 'border-[#dcdadc]' : 'border-[#ecebec]'}`}
                        >
                            <button
                                type="button"
                                aria-expanded={open}
                                onClick={() => toggle(i)}
                                className="cursor-pointer w-full flex items-center gap-4 text-left bg-transparent p-5 max-md:gap-3 max-md:p-3"
                            >
                                <span className="shrink-0 w-14 h-14 max-md:w-8 max-md:h-8 max-md:rounded-lg rounded-xl bg-[#e9fbf4] flex items-center justify-center">
                                    <img alt="" src={img(faq.icon)} className="w-7 h-7 max-md:w-4 max-md:h-4"/>
                                </span>
                                {/* 20px, inheriting the normal weight — the size carries the
                                    hierarchy on its own. Stepped down on mobile, where 20px
                                    wraps the longer questions to three lines between the icon
                                    and the chevron. */}
                                <span className="flex-1 text-xl text-(--text-primary) max-md:text-[13px]">{faq.question}</span>
                                {/* Optional `media`: filenames shown between the question and
                                    the chevron, to preview what the answer is about. Purely
                                    decorative, so it drops out on mobile rather than squeezing
                                    the question into another line. */}
                                {faq.media?.length > 0 && (
                                    <span className="shrink-0 flex items-center gap-2 max-md:hidden">
                                        {faq.media.map((name) => (
                                            <img key={name} alt="" src={img(name)} className="h-10 w-auto"/>
                                        ))}
                                    </span>
                                )}
                                <svg
                                    viewBox="0 0 18 18" fill="none"
                                    className={`shrink-0 w-6 h-6 max-md:w-4 max-md:h-4 transition-transform duration-[420ms] ease-out ${open ? 'rotate-180' : ''}`}
                                >
                                    <path d="M4 7l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            {(open || closing) && (
                                /* Both directions are CSS ANIMATIONS, not transitions: the
                                   panel is mounted only while open (`render()` is a function
                                   precisely so a closed answer doesn't build its content or
                                   fire its requests — the child-seat carousel fetches), so
                                   there is no second rendered state to transition between.
                                   The exit gets its own class and `closingIdx` keeps the
                                   panel alive just long enough to play it. */
                                <div className={open ? 'hopn-faq-reveal' : 'hopn-faq-collapse'}>
                                    {/* The modal contents bring their own padding/width, so the
                                        panel only supplies the card's gutters — no extra indent
                                        that would squeeze wide layouts like the CMR tabs or the
                                        child-seat carousel. */}
                                    {/* `hopn-faq-answer` is the stable hook for FAQ-only
                                        overrides of the shared content (see styles.scss) —
                                        the wrapper above swaps classes between open and
                                        closing, so it can't be styled against. */}
                                    <div className="hopn-faq-answer px-5 pb-5 max-md:px-3 max-md:pb-3 max-md:text-[13px] leading-relaxed">
                                        {faq.render()}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="block text-right">
                <a href={`${(import.meta.env.VITE_BOOKING_PORTAL_URL || '').replace(/\/$/, '')}/faq/`} target="_blank" rel="noopener noreferrer" className="inline-flex gap-3 text-sm font-bold rounded-full bg-(--bg-grey) px-5 py-2.5 mt-10 ml-auto hover:opacity-80 max-md:mt-5 max-md:px-4 max-md:py-2 max-md:text-xs">
                    Browse all FAQ
                    <img src={rightArrow} alt="right Arrow"/>
                </a>
            </div>
        </div>
    )
}
